import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useSession } from '@/client/lib/auth'
import { useStats } from '@/client/modules/plugins/hooks/useStats'
import { useAuthEventsStats } from '@/client/modules/auth-events/hooks/useAuthEvents'
import { useActivitySummary } from '@/client/modules/activity-logs/hooks/useActivitySummary'
import { useActivityLogs, CATEGORY_CONFIG, EVENT_TYPE_LABELS } from '@/client/modules/activity-logs/hooks/useActivityLogs'
import { useAuthStats } from '@/client/modules/sites/hooks/useSites'
import { useSitesStats } from '@/client/modules/sites-registry/hooks/useSitesRegistry'
import {
  Globe,
  AlertTriangle,
  ChevronRight,
  Shield,
  Package,
  Clock,
  UserX,
  CheckCircle2,
  Activity,
  Server,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Dashboard home page — fleet overview, action items, recent activity
 */
export function DashboardPage() {
  const { data: session } = useSession()
  const { data: updateStats, isLoading: updateStatsLoading } = useStats()
  const { data: authEventsStats, isLoading: authEventsLoading } = useAuthEventsStats()
  const { data: activitySummary, isLoading: activityLoading } = useActivitySummary()
  const { data: authStats, isLoading: authStatsLoading } = useAuthStats()
  const { data: sitesStats, isLoading: sitesStatsLoading } = useSitesStats()
  const { data: recentActivity, isLoading: activityFeedLoading } = useActivityLogs({ limit: 8 })

  // Count action items
  const pendingSites = authStats?.pending_sites || 0
  const failedLogins = activitySummary?.failed_logins_24h || 0
  const inactiveSites = activitySummary?.inactive_sites || 0
  const totalActions = pendingSites + (failedLogins > 0 ? 1 : 0) + (inactiveSites > 0 ? 1 : 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          {session?.user?.name ? `Hey ${session.user.name.split(' ')[0]}` : 'Dashboard'}
        </h1>
        <p className="text-sm text-muted-foreground">
          JezPress plugin ecosystem — {sitesStats?.withJezpress || '...'} connected sites
        </p>
      </div>

      {/* Plugin Ecosystem Overview */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MiniStat
          label="Plugin Sites"
          value={sitesStats?.withJezpress}
          isLoading={sitesStatsLoading}
          href="/dashboard/jezpress"
        />
        <MiniStat
          label="Plugins"
          value={updateStats?.plugins}
          suffix="published"
          isLoading={updateStatsLoading}
          href="/dashboard/plugins"
        />
        <MiniStat
          label="Pending"
          value={authStats?.pending_sites}
          suffix="approvals"
          isLoading={authStatsLoading}
          href="/dashboard/jezpress"
        />
        <MiniStat
          label="Active (24h)"
          value={activitySummary?.active_sites_24h}
          suffix="sites"
          isLoading={activityLoading}
        />
      </div>

      {/* Needs Attention — only when there are action items */}
      {(totalActions > 0 || activityLoading || authStatsLoading) && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="p-4 pb-2 md:p-6 md:pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Needs Attention
              {totalActions > 0 && (
                <span className="ml-auto text-xs font-normal text-muted-foreground">
                  {totalActions} item{totalActions !== 1 ? 's' : ''}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
            {(activityLoading || authStatsLoading) ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-3/4" />
              </div>
            ) : totalActions === 0 ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                All clear — nothing needs attention right now
              </div>
            ) : (
              <div className="space-y-2">
                {pendingSites > 0 && (
                  <ActionItem
                    icon={Clock}
                    href="/dashboard/jezpress"
                    title={`${pendingSites} site${pendingSites !== 1 ? 's' : ''} pending approval`}
                    description="New WordPress sites waiting to be approved for OAuth"
                    severity="warning"
                  />
                )}
                {failedLogins > 0 && (
                  <ActionItem
                    icon={UserX}
                    href="/dashboard/logs?tab=auth"
                    title={`${failedLogins} failed login${failedLogins !== 1 ? 's' : ''} in the last 24 hours`}
                    description="Check for brute force attempts or misconfigured sites"
                    severity={failedLogins > 10 ? 'critical' : 'warning'}
                  />
                )}
                {inactiveSites > 0 && (
                  <ActionItem
                    icon={Globe}
                    href="/dashboard/sites"
                    title={`${inactiveSites} site${inactiveSites !== 1 ? 's' : ''} inactive for 7+ days`}
                    description="No activity logged — may need investigation"
                    severity="info"
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Two-column layout: Activity Feed + Stats */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Recent Activity — takes 2 columns */}
        <Card className="lg:col-span-2">
          <CardHeader className="p-4 pb-2 md:p-6 md:pb-3">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Recent Fleet Activity
              </span>
              <Link
                to="/dashboard/logs?tab=activity"
                className="text-xs font-normal text-muted-foreground hover:text-foreground transition-colors"
              >
                View all →
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
            {activityFeedLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Skeleton className="h-5 w-5 rounded-full shrink-0 mt-0.5" />
                    <div className="space-y-1.5 flex-1">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !recentActivity?.logs?.length ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No recent activity</p>
            ) : (
              <div className="space-y-1">
                {recentActivity.logs.map((log) => (
                  <ActivityRow key={log.id} log={log} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right column — stacked stats */}
        <div className="space-y-4">
          {/* Auth Health */}
          <Card>
            <CardHeader className="p-4 pb-2 md:p-6 md:pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <Link to="/dashboard/logs?tab=auth" className="hover:underline">Auth Health</Link>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
              {authEventsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-28" />
                </div>
              ) : !authEventsStats ? (
                <p className="text-sm text-muted-foreground">No data yet</p>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Logins (24h)</span>
                    <span className="font-medium tabular-nums text-green-600 dark:text-green-400">
                      {authEventsStats.loginsToday}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Failures (24h)</span>
                    <span className={cn(
                      'font-medium tabular-nums',
                      authEventsStats.failuresToday > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
                    )}>
                      {authEventsStats.failuresToday}
                    </span>
                  </div>
                  {authEventsStats.loginsToday + authEventsStats.failuresToday > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Success rate</span>
                      <span className="font-medium tabular-nums">
                        {Math.round(
                          (authEventsStats.loginsToday /
                            (authEventsStats.loginsToday + authEventsStats.failuresToday)) * 100
                        )}%
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm pt-1 border-t">
                    <span className="text-muted-foreground">Unique users (7d)</span>
                    <span className="font-medium tabular-nums">{authEventsStats.uniqueUsersWeek}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Fleet Composition */}
          <Card>
            <CardHeader className="p-4 pb-2 md:p-6 md:pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Server className="h-4 w-4" />
                Fleet Composition
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
              {(authStatsLoading || sitesStatsLoading) ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Approved sites</span>
                    <span className="font-medium tabular-nums">{authStats?.approved_sites || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Pending</span>
                    <span className={cn(
                      'font-medium tabular-nums',
                      (authStats?.pending_sites || 0) > 0 && 'text-amber-600 dark:text-amber-400'
                    )}>
                      {authStats?.pending_sites || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm pt-1 border-t">
                    <span className="text-muted-foreground">Total logins</span>
                    <span className="font-medium tabular-nums">{authStats?.total_logins?.toLocaleString() || 0}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Update Server */}
          <Card>
            <CardHeader className="p-4 pb-2 md:p-6 md:pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Package className="h-4 w-4" />
                <Link to="/dashboard/plugins" className="hover:underline">Update Server</Link>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-0 md:p-6 md:pt-0">
              {updateStatsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Plugins</span>
                    <span className="font-medium tabular-nums">{updateStats?.plugins || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Themes</span>
                    <span className="font-medium tabular-nums">{updateStats?.themes || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Licenses</span>
                    <span className="font-medium tabular-nums">{updateStats?.licenses || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm pt-1 border-t">
                    <span className="text-muted-foreground">Downloads</span>
                    <span className="font-medium tabular-nums">{updateStats?.downloads?.toLocaleString() || 0}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

/**
 * Compact stat for the overview row
 */
function MiniStat({
  label,
  value,
  suffix,
  isLoading,
  href,
  className,
}: {
  label: string
  value: number | undefined
  suffix?: string
  isLoading: boolean
  href?: string
  className?: string
}) {
  const content = (
    <Card className={cn(
      'transition-colors',
      href && 'hover:bg-accent/50 cursor-pointer',
      className,
    )}>
      <CardContent className="p-3 md:p-4">
        {isLoading ? (
          <Skeleton className="h-7 w-12 mb-1" />
        ) : (
          <div className="text-xl md:text-2xl font-bold tabular-nums">{value?.toLocaleString() ?? '—'}</div>
        )}
        <p className="text-xs text-muted-foreground truncate">
          {suffix ? `${label}` : label}
        </p>
      </CardContent>
    </Card>
  )

  return href ? <Link to={href}>{content}</Link> : content
}

/**
 * Action item in the "Needs Attention" section
 */
function ActionItem({
  icon: Icon,
  href,
  title,
  description,
  severity,
}: {
  icon: React.ComponentType<{ className?: string }>
  href: string
  title: string
  description: string
  severity: 'critical' | 'warning' | 'info'
}) {
  return (
    <Link
      to={href}
      className="flex items-start gap-3 p-2.5 -mx-1 rounded-lg hover:bg-accent/50 transition-colors group"
    >
      <Icon className={cn(
        'h-5 w-5 shrink-0 mt-0.5',
        severity === 'critical' && 'text-red-500',
        severity === 'warning' && 'text-amber-500',
        severity === 'info' && 'text-blue-500',
      )} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
    </Link>
  )
}

/**
 * Single row in the activity feed
 */
function ActivityRow({ log }: { log: { id: string; site_domain: string; event_type: string; category: string; object_name: string | null; user_email: string | null; created_at: string } }) {
  const categoryConfig = CATEGORY_CONFIG[log.category]
  const eventLabel = EVENT_TYPE_LABELS[log.event_type] || log.event_type

  // Build a human-readable description
  const description = buildDescription(log)

  // Relative time
  const timeAgo = getRelativeTime(log.created_at)

  return (
    <div className="flex items-start gap-2.5 py-2 border-b last:border-0">
      {/* Category dot */}
      <div className={cn(
        'h-2 w-2 rounded-full shrink-0 mt-1.5',
        categoryConfig?.color || 'bg-gray-400',
      )} />
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate">
          <span className="font-medium">{eventLabel}</span>
          {description && <span className="text-muted-foreground"> — {description}</span>}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {log.site_domain}
          {log.user_email && ` · ${log.user_email}`}
          <span className="ml-2">{timeAgo}</span>
        </p>
      </div>
    </div>
  )
}

/**
 * Build a concise description from log data
 */
function buildDescription(log: { event_type: string; object_name: string | null; user_email: string | null }): string {
  const { event_type, object_name } = log

  if (object_name) {
    if (event_type.startsWith('plugin_')) return object_name
    if (event_type.startsWith('theme_')) return object_name
    if (event_type === 'option_updated') return object_name
  }

  return ''
}

/**
 * Convert timestamp to relative time string
 */
function getRelativeTime(timestamp: string): string {
  const now = Date.now()
  const then = new Date(timestamp).getTime()
  const diffMs = now - then

  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  return `${days}d ago`
}
